// Token readers for loop placeholder expressions.
// Example: `bp.size`, `'x'`, and `12px` are parsed here.
import type { LoopExpressionToken } from './placeholder-expression-types'

// Build one token from a single operator or parenthesis character.
export function buildSingleCharToken(char: string): LoopExpressionToken {
  // Switch the token kind by the scanned single char.
  switch (char) {
    case '+':
      // Return the plus-operator token.
      return { kind: 'plus', value: '+' }
    case '-':
      // Return the minus-operator token.
      return { kind: 'minus', value: '-' }
    case '*':
      // Return the multiplication token.
      return { kind: 'star', value: '*' }
    case '/':
      // Return the division token.
      return { kind: 'slash', value: '/' }
    case '(':
      // Return the opening-parenthesis token.
      return { kind: 'paren-open', value: '(' }
    default:
      // Return the closing-parenthesis token.
      return { kind: 'paren-close', value: ')' }
  }
}

// Read one quoted string token and keep escaped quotes intact.
export function readStringToken(source: string, start: number, quote: "'" | '"'): { value: string; end: number } | null {
  let value = ''

  // Consume quoted characters until the closing quote is found.
  for (let index = start + 1; index < source.length; index += 1) {
    const char = source[index]
    const previous = source[index - 1] ?? ''
    if (char === quote && previous !== '\\') {
      // Return the parsed quoted string token.
      return { value, end: index + 1 }
    }
    value += char
  }

  // Return null for unterminated string literals.
  return null
}

// Read one numeric token with optional decimal and unit suffix.
export function readNumberToken(source: string, start: number): { value: string; end: number } {
  let end = start
  let sawDot = false

  // Consume the numeric body before the optional unit suffix.
  while (end < source.length) {
    const char = source[end]
    if (isDigit(char)) {
      end += 1
      continue
    }
    if (char === '.' && !sawDot) {
      sawDot = true
      end += 1
      continue
    }
    break
  }

  // Consume a plain alphabetic or percent unit suffix.
  while (end < source.length && isUnitChar(source[end])) {
    end += 1
  }

  // Return the parsed numeric token slice.
  return { value: source.slice(start, end), end }
}

// Read one dotted identifier token like `bp.size`.
export function readIdentifierToken(source: string, start: number): { value: string; end: number } {
  let end = start + 1

  // Consume identifier chars and dotted member segments.
  while (end < source.length) {
    const char = source[end]
    const next = source[end + 1] ?? ''
    if (isIdentifierBody(char)) {
      end += 1
      continue
    }
    if (char === '.' && isIdentifierStart(next)) {
      end += 1
      continue
    }
    break
  }

  // Return the parsed identifier token slice.
  return { value: source.slice(start, end), end }
}

// Check whether a char is parser whitespace.
export function isWhitespace(char: string): boolean {
  // Return true for ASCII whitespace chars.
  return char === ' ' || char === '\t' || char === '\n' || char === '\r' || char === '\f'
}

// Check whether a char is a decimal digit.
export function isDigit(char: string): boolean {
  // Return true for `0..9` characters.
  return char >= '0' && char <= '9'
}

// Check whether a char can start an identifier token.
export function isIdentifierStart(char: string): boolean {
  // Return true for alphabetic and underscore starters.
  return (char >= 'a' && char <= 'z') || (char >= 'A' && char <= 'Z') || char === '_'
}

// Check whether a char can continue an identifier token.
export function isIdentifierBody(char: string): boolean {
  // Return true for identifier body characters.
  return isIdentifierStart(char) || isDigit(char)
}

// Check whether a char can continue a CSS-like unit suffix.
function isUnitChar(char: string): boolean {
  // Return true for alphabetic unit chars and `%`.
  return ((char >= 'a' && char <= 'z') || (char >= 'A' && char <= 'Z') || char === '%')
}
